<script setup lang="ts">
import { ref } from 'vue'
import Attachments from '@/components/Attachments.vue'
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/foundation/form'
import { filterValidFiles } from '@/utils/utils'
import AutoFormLabel from './AutoFormLabel.vue'
import type { FieldProps } from './interface'
import { beautifyObjectName } from './utils'

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const dragCounter = ref(0)

function openFileDialog() {
  fileInput.value?.click()
}

function handleDragEnter(ev: DragEvent) {
  ev.preventDefault()
  dragCounter.value++
  isDragging.value = true
}

function handleDragLeave(ev: DragEvent) {
  ev.preventDefault()
  dragCounter.value--
  if (dragCounter.value <= 0) {
    isDragging.value = false
    dragCounter.value = 0
  }
}

function handleDrop(ev: DragEvent, onInput: (value: any) => void) {
  ev.preventDefault()
  dragCounter.value = 0
  isDragging.value = false
  const droppedFiles = ev.dataTransfer?.files

  if (droppedFiles && droppedFiles.length > 0) {
    const validFiles = filterValidFiles(Array.from(droppedFiles))

    if (!validFiles.length) return

    for (const file of Array.from(droppedFiles)) {
      files.value.push(createFileMeta(file))
    }
    onInput(files.value)
  }
}

defineProps<FieldProps>()

type FileMeta = {
  fileName: string
  mimetype: string
  size: number
  type: string
  file: File
}

const files = ref<FileMeta[]>([])

function createFileMeta(file: File): FileMeta {
  return {
    fileName: file.name,
    mimetype: file.type,
    size: file.size,
    type: 'Internal',
    file,
  }
}

function handleFilesChange(ev: InputEvent, onInput: (value: any) => void) {
  const input = ev.target as HTMLInputElement

  if (input.files) {
    const validFiles = filterValidFiles(Array.from(input.files))

    if (!validFiles.length) return

    for (const file of validFiles) {
      files.value.push(createFileMeta(file))
    }
    onInput(files.value)
  }
}

function removeFile(index: number, onInput: (value: any) => void) {
  files.value.splice(index, 1)
  onInput(files.value)
}
</script>

<template>
  <FormField v-slot="slotProps" :name="fieldName">
    <FormItem v-bind="$attrs">
      <AutoFormLabel v-if="!config?.hideLabel" :required="required">
        {{ config?.label || beautifyObjectName(label ?? fieldName) }}
      </AutoFormLabel>
      <FormControl>
        <slot v-bind="slotProps">
          <div
            :class="[
              `w-full lg:w-1/2 flex items-center justify-center rounded-md gap-2 border-dashed border-2 p-4 py-2 text-center transition-all duration-200 ${config?.inputProps?.class || ''}`,
              isDragging ? 'bg-slate-100 border-slate-400' : 'border-border hover:border-slate-300/30',
            ]"
            @dragover.prevent
            @dragenter="handleDragEnter"
            @dragleave="handleDragLeave"
            @drop.prevent="(ev) => handleDrop(ev, slotProps.componentField.onInput)"
          >
            <p class="text-muted-foreground text-sm">Drop file here or</p>
            <button
              type="button"
              class="bg-slate-200 hover:bg-slate-300 py-1 px-3 text-sm rounded font-medium"
              @click="openFileDialog"
            >
              Browse
            </button>
            <input
              ref="fileInput"
              type="file"
              multiple
              v-bind="{ ...config?.inputProps }"
              :disabled="disabled"
              class="hidden"
              @change="(ev: InputEvent) => handleFilesChange(ev, slotProps.componentField.onInput)"
            />
          </div>
          <Attachments
            :files="files"
            :local-image="true"
            :show-label="false"
            @remove="(index) => removeFile(index, slotProps.componentField.onInput)"
          />
        </slot>
      </FormControl>
      <FormDescription v-if="config?.description">
        {{ config.description }}
      </FormDescription>
      <FormMessage />
    </FormItem>
  </FormField>
</template>
